import { useState } from 'react'

import {
  Member,
  MemberListFilterByOperator,
} from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { MemberGroups } from '@tribeplatform/react-components/CMS'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useMembers, useUpdateMember } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { SettingsLayout } from '../Layout/SettingsLayout.js'

const FLAGGED_MEMBERS_LIMIT = 100

export const FlaggedMembers = () => {
  const { $t } = useI18n()
  const { completeStep } = useAdminOnboarding()
  const [loading, setLoading] = useState(false)
  const { mutateAsync: updateMember } = useUpdateMember()
  const { data, isInitialLoading: isLoadingFlaggedMembers } = useMembers({
    fields: {
      profilePicture: 'basic',
    },
    variables: {
      limit: FLAGGED_MEMBERS_LIMIT,
      filterBy: [
        {
          key: 'flagged',
          operator: MemberListFilterByOperator.equals,
          value: 'true',
        },
      ],
    },
    useInfiniteQueryOptions: {
      cacheTime: 0,
    },
  })

  const flaggedMembers = simplifyPaginatedResult<Member>(data)?.nodes ?? []
  const enable = flaggedMembers?.length > 0

  const submitFlaggedMembers = async data => {
    const { flaggedMembers: updatedMembers, enable } = data
    if (!updatedMembers) {
      return
    }

    setLoading(true)

    const flagMemberPromises = updatedMembers
      .filter(
        member =>
          enable &&
          !flaggedMembers.some(flaggedMember => flaggedMember.id === member.id),
      )
      .map(member =>
        updateMember({
          id: member.id,
          input: {
            flagged: true,
          },
        }),
      )

    const unflagMemberPromises = flaggedMembers
      .filter(
        member => !enable || !updatedMembers.some(m => m.id === member.id),
      )
      .map(member =>
        updateMember({
          id: member.id,
          input: {
            flagged: false,
          },
        }),
      )

    try {
      await Promise.all([...flagMemberPromises, ...unflagMemberPromises])
      completeStep({
        step: 'moderation-rules',
      })
      toast({
        title: $t({
          defaultMessage: 'Moderation settings updated.',
          id: 'Admin.ModerationSettings.Updated',
        }),
        status: 'success',
      })
    } catch {
      toast({
        title: $t({
          defaultMessage: 'Unable to update moderation settings.',
          id: 'Admin.ModerationSettings.FailedToUpdate',
        }),
        status: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  if (isLoadingFlaggedMembers) {
    return null
  }

  return (
    <Form
      defaultValues={{
        flaggedMembers,
        enable,
      }}
      onSubmit={submitFlaggedMembers}
    >
      {({ formState: { isDirty }, watch, reset }) => (
        <SettingsLayout
          title={$t({
            defaultMessage: '{MEMBERS_CC} watchlist',
            id: 'Admin.ModerationSettings.MEMBERSWatchlist.Title',
          })}
          subtitle={$t({
            defaultMessage:
              'Select specific {MEMBERS} to have all their content reviewed before it is made public.',
            id: 'Admin.ModerationSettings.FlaggedMembers.Description',
          })}
          disabled={!isDirty}
          isLoading={loading}
          onCancel={() => reset()}
          saveLabel={<T defaultMessage="Update" id="Generics.Update" />}
        >
          <Form.Layout>
            <Alert status="warning">
              <T
                defaultMessage="Posts and replies from flagged members will be held for moderation before being published. Use this to monitor members who may violate community guidelines."
                id="Admin.ModerationSettings.FlaggedMembers.Warning"
              />
            </Alert>
            {data?.pages?.[0]?.totalCount > FLAGGED_MEMBERS_LIMIT && (
              <Alert
                status="info"
                className="mb-3"
                title={$t(
                  {
                    defaultMessage:
                      'You have more than {flagged_members_count, number} flagged {MEMBERS}',
                    id: 'Admin.ModerationSettings.FlaggedMembers.Alert.Title',
                  },
                  {
                    flagged_members_count: FLAGGED_MEMBERS_LIMIT,
                  },
                )}
              >
                <T
                  defaultMessage="Some flagged members might not be visible here, please use the {MEMBERS} section."
                  id="Admin.ModerationSettings.FlaggedMembers.Alert.Description"
                />
              </Alert>
            )}
            <Form.Toggle
              name="enable"
              label={$t({
                defaultMessage: 'Enable members watchlist',
                id: 'Admin.ModerationSettings.EnableMembersWatchlist',
              })}
            />
            <LoadingOverlay isLoading={!watch('enable')} pulse={false}>
              <Form.MemberPicker
                multiple
                name="flaggedMembers"
                memberGroup={MemberGroups.member}
              />
            </LoadingOverlay>
          </Form.Layout>
        </SettingsLayout>
      )}
    </Form>
  )
}
